import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthContext';
import { finishOidcLogin } from '@/modules/auth/oidc/pkce';
import { getPlatformApiUrl } from '@/core/api';
import { mapPlatformMeToSessionUser } from '@/core/auth/sessionMap';

/**
 * OIDC redirect callback — exchanges code for tokens, loads /v1/me, establishes session.
 */
const AuthCallbackPage = () => {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tokens = await finishOidcLogin(search);
        const meRes = await fetch(`${getPlatformApiUrl()}/v1/me`, {
          headers: { Authorization: `Bearer ${tokens.access_token}` },
        });
        if (!meRes.ok) {
          const body = await meRes.text();
          throw new Error(`Platform /v1/me failed (${meRes.status}): ${body}`);
        }
        const envelope = await meRes.json();
        const me = envelope.data || envelope;
        const sessionUser = mapPlatformMeToSessionUser(me);
        if (cancelled) return;
        login(tokens.access_token, sessionUser);
        navigate('/dashboard', { replace: true });
      } catch (e) {
        if (!cancelled) setError(e.message || 'OIDC login failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search, login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ops-bg">
      <div className="max-w-md w-full rounded-xl border border-ops-line bg-ops-panel p-6 text-center">
        {error ? (
          <>
            <p className="text-red-600 mb-4">{error}</p>
            <a className="text-ops-cy" href="/login">Back to login</a>
          </>
        ) : (
          <p className="text-ops-ink2">Completing sign-in…</p>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
